import { useMemo } from "react"
import qs from "qs"
import { BoltIcon, ChevronDownIcon } from "@heroicons/react/24/outline"
import { DateRangePicker } from "~/ui-rtk/components/ui/controls"
import DropdownMenu from "~/ui-rtk/components/ui/controls/DropdownMenu/DropdownMenu"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import { AggregationType } from "~/ui-rtk/constants/charts"
import useDate from "~/ui-rtk/hooks/date"
import { H1 } from "~/ui-rtk/components/ui/typography"
import TableFiltersBar from "~/ui-rtk/components/ui/controls/FiltersBar/TableFiltersBar"
import type { TFilterColumnConfig } from "~/ui-rtk/components/ui/controls/TableFilters/types"
import type { TableVisualizationColumnFilter } from "~/ui-rtk/constants/table-visualization-types"
import { useURLSearchParams } from "~/ui-rtk/hooks/url"
import useUrlColumnFilters from "~/ui-rtk/hooks/url-column-filters"
import useCubeFiltersFromColumnFilters from "~/ui-rtk/hooks/cube-filters-from-column-filters"
import { useGetTagCategoriesQuery } from "~/ui-rtk/api/tagCategoriesApi"
import Report from "./components/Report"
import {
  DEFAULT_TAXONOMY_CATEGORIES,
  getReportSections,
  getTaxonomyVerticals,
  TAXONOMY_PRESETS,
  type TTaxonomyCategory,
  type TTaxonomyPreset,
  UNTAGGED_LABEL,
  VIEW,
} from "./connect"

const dayjs = useDate()
const maxDateRestriction = dayjs().toDate()

// URL-state id for the page-level filters (shared by every section).
const PAGE_FILTERS_ID = "creative-taxonomy"

// URL param holding the active TOF/DR preset key.
const PRESET_URL_KEY = "taxonomy_preset"

// Filter ids owned by presets: applying a preset replaces filters on these
// ids, so switching TOF -> DR (or back to Default) doesn't leave the previous
// preset's initiative filter behind.
const PRESET_FILTER_IDS = new Set(["initiative_type"])

// Non-taxonomy fields exposed in the global filters bar. `text` types render
// keyword (contains/excludes) filters; `currency`/`number` render min/max
// ranges. The taxonomy fields are prepended per company (see getFilterColumns)
// so the bar offers exactly the categories the sections group by, under the
// company's own labels.
const BASE_FILTER_COLUMNS: TFilterColumnConfig[] = [
  { key: "media_channel", header: "Platform", type: "text" },
  { key: "initiative_type", header: "Initiative Type", type: "text" },
  { key: "campaign_name", header: "Campaign Name", type: "text" },
  { key: "adset_name", header: "Ad Set Name", type: "text" },
  { key: "ad_name", header: "Ad Name", type: "text" },
  { key: "spend", header: "Spend", type: "currency" },
  { key: "brand_value_over_spend", header: "Brand ROAS", type: "number" },
  { key: "revenue_from_search_roas", header: "Search ROAS", type: "number" },
]

// Page-wide keyword filters exist only for column-backed categories: a custom
// category's value lives behind the per-section slug selector, so there is no
// single page-level member a keyword filter could target.
const getFilterColumns = (
  categories: TTaxonomyCategory[],
): TFilterColumnConfig[] => [
  ...getTaxonomyVerticals(categories)
    .filter(({ kind }) => kind === "column")
    .map(({ dimKey, label }) => ({
      key: dimKey,
      header: label,
      type: "text",
    })),
  ...BASE_FILTER_COLUMNS,
]

export default function CreativeTaxonomyReportPage() {
  // The sections and taxonomy filters follow the company's tag categories
  // (renamed labels, custom ordering). Until they load, fall back to the
  // system defaults so the page doesn't mount empty and then reflow.
  const { data: tagCategories } = useGetTagCategoriesQuery()
  const categories: TTaxonomyCategory[] =
    tagCategories ?? DEFAULT_TAXONOMY_CATEGORIES
  const filterColumns = useMemo(
    () => getFilterColumns(categories),
    [categories],
  )

  const { dateRange, compareRange } = useMinMaxDateRange(
    undefined,
    maxDateRestriction,
    AggregationType.DAILY,
    false,
    undefined,
    "last30Days",
  )
  const { setPeriod, setComparePeriod } = usePeriod()
  const { params, setParams } = useURLSearchParams()
  const { urlKey: filtersUrlKey, columnFilters } =
    useUrlColumnFilters(PAGE_FILTERS_ID)

  // Cube filters shared by every section's query (prefixed with the view name).
  const extraFilters = useMemo(
    () => useCubeFiltersFromColumnFilters(VIEW, columnFilters),
    [columnFilters],
  )

  const activePreset = useMemo(
    () => TAXONOMY_PRESETS.find(({ key }) => key === params[PRESET_URL_KEY]),
    [params],
  )

  const sections = useMemo(
    () => getReportSections(activePreset, categories),
    [activePreset, categories],
  )

  // Preset key + its initiative filter must go out in ONE setParams call —
  // consecutive functional setSearchParams calls from different hook instances
  // each start from the pre-click URL, so the last write would drop the others
  // (same fix as CreativeReportsV2.applyMetricsPreset). The filter is encoded
  // exactly like useUrlParamsState encodes it so useUrlColumnFilters parses it.
  const applyPreset = (preset?: TTaxonomyPreset) => {
    const nextFilters: TableVisualizationColumnFilter[] = [
      ...(columnFilters ?? []).filter(({ id }) => !PRESET_FILTER_IDS.has(id)),
      ...(preset
        ? [
            {
              id: "initiative_type",
              variant: "text" as const,
              operator: "contains" as const,
              value: preset.initiativeContains,
            },
          ]
        : []),
    ]
    setParams({
      [PRESET_URL_KEY]: preset?.key,
      [filtersUrlKey]:
        qs.stringify(nextFilters, {
          arrayFormat: "indices",
          allowEmptyArrays: true,
        }) || undefined,
    })
  }

  const changePeriod = ({
    start,
    end,
    compareStart,
    compareEnd,
  }: {
    start: Date
    end: Date
    compareStart?: Date | null
    compareEnd?: Date | null
  }) => {
    void setPeriod(
      dayjs(start).format(DATE_FORMAT),
      dayjs(end).format(DATE_FORMAT),
    )
    if (compareStart && compareEnd) {
      setComparePeriod(
        dayjs(compareStart).format(DATE_FORMAT),
        dayjs(compareEnd).format(DATE_FORMAT),
      )
    } else {
      setComparePeriod()
    }
  }

  const rangeReady =
    dateRange?.[0] &&
    dateRange?.[1] &&
    isValidDate(dateRange[0]) &&
    isValidDate(dateRange[1])

  return (
    <div className="creative-taxonomy-report-page h-full overflow-y-visible px-system-md pb-system-2xl">
      <div className="mt-6 flex justify-between items-center gap-system-sm">
        <div>
          <H1 className="text-white font-bold text-2xl">
            Creative Taxonomy Report
          </H1>
          <p className="text-sm text-system-gray-400 mt-1">
            Paid-media performance sliced by curated creative tags. Brand ROAS
            is the 180-day window; brand_search uses Search ROAS.
          </p>
        </div>
        <div className="flex items-stretch gap-system-sm">
          <DropdownMenu
            options={[
              { label: "Default", value: "" },
              ...TAXONOMY_PRESETS.map(({ key, label }) => ({
                label,
                value: key,
              })),
            ]}
            onItemClick={value =>
              applyPreset(TAXONOMY_PRESETS.find(({ key }) => key === value))
            }
            className="cursor-pointer bg-system-blue-700 border border-system-blue-600 text-base rounded-system-md"
          >
            <div className="flex gap-2 items-center justify-center">
              <BoltIcon className="w-5 h-5 text-white" />
              <div>Preset: {activePreset?.label ?? "Default"}</div>
              <ChevronDownIcon className="w-system-md h-system-md text-white" />
            </div>
          </DropdownMenu>
          <TableFiltersBar id={PAGE_FILTERS_ID} columnConfig={filterColumns} />
          {rangeReady && (
            <DateRangePicker
              start={dateRange[0]}
              end={dateRange[1]}
              comparisonEnabled
              compareStart={
                compareRange?.[0] && isValidDate(compareRange[0])
                  ? compareRange[0]
                  : undefined
              }
              compareEnd={
                compareRange?.[1] && isValidDate(compareRange[1])
                  ? compareRange[1]
                  : undefined
              }
              maxDate={maxDateRestriction}
              onChange={changePeriod}
            />
          )}
        </div>
      </div>

      {rangeReady && (
        <div className="mt-system-xl flex flex-col gap-system-2xl">
          {sections.map(section => (
            <Report
              key={section.id}
              title={section.title}
              description={section.description}
              query={section.query}
              chart={section.chart}
              columns={section.columns}
              extraFilters={extraFilters}
              chartId={`creative-taxonomy-${section.id}`}
              filenameBase={`creative-taxonomy-${section.id}`}
              dateRange={dateRange as [Date, Date]}
              compareRange={compareRange ?? undefined}
              comparisonEnabled={Boolean(compareRange)}
              emptyDimensionLabel={UNTAGGED_LABEL}
              tagSlug={section.tagSlug}
            />
          ))}
        </div>
      )}
    </div>
  )
}
